import { io } from 'socket.io-client';

let socket;

// Subscribers for connection status changes
const connectionListeners = new Set();
let currentStatus = 'unknown'; // 'connected' | 'disconnected' | 'reconnecting' | 'unknown'

function notifyConnectionStatus(status) {
  currentStatus = status;
  for (const cb of connectionListeners) cb(status);
}

const connectSocket = (token) => {
  if (socket && socket.connected) {
    return socket;
  }

  // Empty VITE_SOCKET_BASE_URL → same origin (Vite proxy handles /socket.io)
  const socketUrl = import.meta.env.VITE_SOCKET_BASE_URL || window.location.origin;

  socket = io(socketUrl, {
    auth: { token },
    transports: ['websocket', 'polling'],
    secure: window.location.protocol === 'https:',
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 10,
  });

  socket.on('connect', () => notifyConnectionStatus('connected'));
  socket.on('disconnect', () => notifyConnectionStatus('disconnected'));
  socket.on('reconnect_attempt', () => notifyConnectionStatus('reconnecting'));
  socket.on('reconnect', () => notifyConnectionStatus('connected'));
  socket.on('reconnect_failed', () => notifyConnectionStatus('failed'));

  return socket;
};

const getSocket = () => {
  return socket;
};

const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

const startBotGame = (deck, seed) => {
  const s = getSocket();
  if (!s) throw new Error('Socket not connected');
  s.emit('start_bot_game', { deck, seed: Number.isInteger(seed) ? seed : undefined });
};

const sendGameAction = (action, params = {}) => {
  const s = getSocket();
  if (!s) throw new Error('Socket not connected');
  s.emit('game_action', { action, params });
};

const onGameStateUpdate = (callback) => {
  const s = getSocket();
  if (!s) return () => {};
  s.on('game_state_update', callback);
  return () => s.off('game_state_update', callback);
};

const onGameError = (callback) => {
  const s = getSocket();
  if (!s) return () => {};
  s.on('game_error', callback);
  return () => s.off('game_error', callback);
};

const onConnectionStatus = (callback) => {
  connectionListeners.add(callback);
  // Fire immediately with current status so late subscribers get current state
  if (currentStatus !== 'unknown') callback(currentStatus);
  return () => connectionListeners.delete(callback);
};

const onOpponentDisconnected = (callback) => {
  const s = getSocket();
  if (!s) return () => {};
  s.on('opponent_disconnected', callback);
  return () => s.off('opponent_disconnected', callback);
};

const onOpponentForfeit = (callback) => {
  const s = getSocket();
  if (!s) return () => {};
  s.on('opponent_forfeit', callback);
  return () => s.off('opponent_forfeit', callback);
};

const rejoinBotGame = () => {
  const s = getSocket();
  if (!s) return;
  s.emit('rejoin_bot_game');
};

const sendMulligan = (indices) => {
  const s = getSocket();
  if (!s) throw new Error('Socket not connected');
  s.emit('mulligan', { indices });
};

const selectBattlefield = (chosenIndex) => {
  const s = getSocket();
  if (!s) throw new Error('Socket not connected');
  s.emit('select_battlefield', { chosenIndex });
};

const chooseStartingPlayer = (playerId) => {
  const s = getSocket();
  if (!s) throw new Error('Socket not connected');
  s.emit('choose_starting_player', { playerId });
};

export {
  connectSocket, getSocket, disconnectSocket,
  startBotGame, sendGameAction,
  onGameStateUpdate, onGameError,
  onConnectionStatus, onOpponentDisconnected, onOpponentForfeit,
  sendMulligan, selectBattlefield, chooseStartingPlayer,
  rejoinBotGame,
};
